/**
 * Impression ticket thermique 80 mm — ordonnance type formulaire clinique.
 * Encre noire uniquement : titre, référence code-barres, champs patient et signature.
 */

import { renderPrescriptionBarcodeCanvas } from './prescriptionBarcode';

const INK = '#000000';
const PAPER = '#FFFFFF';
const FONT_FAMILY = 'Arial, Helvetica, sans-serif';
const PRINT_FRAME_ID = 'prescription-thermal-print-frame';
const WIDTH_MM = 80;

function dotsPerMm() {
  return 8;
}

function font(sizePx, weight = 'bold', italic = false) {
  const style = italic ? 'italic ' : '';
  return `${style}${weight} ${sizePx}px ${FONT_FAMILY}`;
}

function posologyFont(sizePx) {
  return `italic 400 ${sizePx}px Georgia, 'Times New Roman', serif`;
}

function lineHeight(sizePx) {
  return Math.round(sizePx * 1.35);
}

function loadLogo(src) {
  const domLogo = document.querySelector('.rx-premium__logo');
  if (domLogo?.complete && domLogo.naturalWidth > 0) {
    return Promise.resolve(domLogo);
  }

  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('no logo'));
      return;
    }
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapLines(ctx, text, maxWidth) {
  const raw = String(text || '').trim();
  if (!raw) return [];

  const lines = [];
  let current = '';

  raw.split(/\s+/).forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function drawInkText(ctx, text, x, y, sizePx, { heavy = false } = {}) {
  ctx.fillStyle = INK;
  if (heavy) {
    ctx.strokeStyle = INK;
    ctx.lineWidth = Math.max(0.75, sizePx / 24);
    ctx.lineJoin = 'round';
    ctx.strokeText(text, x, y);
  }
  ctx.fillText(text, x, y);
}

function drawInkLines(ctx, lines, x, y, sizePx, opts = {}) {
  const lh = lineHeight(sizePx);
  let cursor = y;
  lines.forEach((line) => {
    drawInkText(ctx, line, x, cursor, sizePx, opts);
    cursor += lh;
  });
  return cursor;
}

function drawCenteredInkLines(ctx, lines, y, sizePx, pad, inner, opts = {}) {
  const lh = lineHeight(sizePx);
  let cursor = y;
  lines.forEach((line) => {
    const w = ctx.measureText(line).width;
    const x = pad + Math.max(0, (inner - w) / 2);
    drawInkText(ctx, line, x, cursor, sizePx, opts);
    cursor += lh;
  });
  return cursor;
}

/** Bandeau haut / bas type formulaire (remplace les vagues décoratives). */
function drawFormBand(ctx, pad, inner, y, height = 6) {
  ctx.fillStyle = INK;
  ctx.fillRect(pad, y, inner, height);
  return y + height + 8;
}

function drawRule(ctx, pad, inner, y, { thick = false, double = false } = {}) {
  ctx.fillStyle = INK;
  if (double) {
    ctx.fillRect(pad, y, inner, 2);
    ctx.fillRect(pad, y + 5, inner, 1);
    return y + 10;
  }
  ctx.fillRect(pad, y, inner, thick ? 2 : 1);
  return y + (thick ? 8 : 6);
}

/**
 * Champ formulaire : libellé + valeur sur ligne soulignée.
 * Ex. Nom  ________________valeur________________
 */
function getPatientInfoRows(patient, dateValue) {
  const rows = [{ label: 'Nom', value: patient.name || '—' }];
  if (patient.detail) rows.push({ label: 'Infos', value: patient.detail });
  if (dateValue) rows.push({ label: 'Date', value: dateValue });
  return rows;
}

function measurePatientInfoBox(ctx, rows, inner, sizes) {
  const labelWidth = 112;
  const valueWidth = inner - 32 - labelWidth;
  ctx.font = font(sizes.fieldValue, '500');
  const rowHeights = rows.map((row) => {
    const lines = Math.max(1, wrapLines(ctx, row.value, valueWidth).length);
    return Math.max(lineHeight(sizes.fieldLabel), lines * lineHeight(sizes.fieldValue)) + 10;
  });
  return rowHeights.reduce((sum, rowHeight) => sum + rowHeight, 0) + 24;
}

function drawPatientInfoBox(ctx, rows, y, pad, inner, sizes) {
  const labelWidth = 112;
  const boxX = pad;
  const boxWidth = inner;
  const valueX = boxX + 16 + labelWidth;
  const valueWidth = boxWidth - labelWidth - 32;
  const rowHeights = rows.map((row) => {
    ctx.font = font(sizes.fieldValue, '500');
    const lines = Math.max(1, wrapLines(ctx, row.value, valueWidth).length);
    return Math.max(lineHeight(sizes.fieldLabel), lines * lineHeight(sizes.fieldValue)) + 10;
  });
  const boxHeight = rowHeights.reduce((sum, rowHeight) => sum + rowHeight, 0) + 24;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(boxX + 0.5, y + 0.5, boxWidth - 1, boxHeight - 1);

  let cursor = y + 12;
  rows.forEach((row, index) => {
    ctx.font = font(sizes.fieldLabel, '700');
    drawInkText(ctx, row.label, boxX + 12, cursor, sizes.fieldLabel);
    ctx.font = font(sizes.fieldValue, '500');
    const lines = wrapLines(ctx, row.value, valueWidth);
    drawInkLines(ctx, lines, valueX, cursor, sizes.fieldValue);
    cursor += rowHeights[index];
  });
  return y + boxHeight + 12;
}

function thresholdCanvas(canvas) {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  const image = ctx.getImageData(0, 0, width, height);
  const { data } = image;

  for (let i = 0; i < data.length; i += 4) {
    const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    const v = lum < 248 ? 0 : 255;
    data[i] = v;
    data[i + 1] = v;
    data[i + 2] = v;
    data[i + 3] = 255;
  }

  ctx.putImageData(image, 0, 0);
}

function splitSloganTwoLines(slogan, sloganLines) {
  if (Array.isArray(sloganLines) && sloganLines.length) {
    return sloganLines.map((line) => String(line || '').trim()).filter(Boolean).slice(0, 2);
  }
  const raw = String(slogan || '').trim();
  if (!raw) return [];
  const colon = raw.indexOf(':');
  if (colon > 0 && colon < raw.length - 1) {
    return [raw.slice(0, colon + 1).trim(), raw.slice(colon + 1).trim()];
  }
  const words = raw.split(/\s+/);
  if (words.length < 2) return [raw];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

function getSizes() {
  return {
    site: 18,
    slogan: 21,
    docTitle: 34,
    doctor: 22,
    docNumber: 25,
    fieldLabel: 19,
    fieldValue: 31,
    section: 17,
    med: 37,
    qty: 24,
    posology: 30,
    notesLabel: 18,
    notes: 25,
    stampLabel: 18,
    stampDoctor: 24,
    legal: 18
  };
}

function getLogoHeight() {
  return 160;
}

function getStampHeight() {
  return 240;
}

function resolvePatientFields(data) {
  if (data.patientName) {
    return {
      name: data.patientName,
      detail: data.patientDetailLine || ''
    };
  }
  return {
    name: data.patientLine || '—',
    detail: ''
  };
}

function resolveMedicationLines(med) {
  if (med.indexName) {
    return {
      indexName: med.indexName,
      qtyLine: med.qtyLine || '',
      detailLines: med.detailLines || []
    };
  }
  return {
    indexName: med.titleLine || '',
    qtyLine: '',
    detailLines: med.detailLines || []
  };
}

function measureBlock(ctx, text, inner, sizePx, gapAfter) {
  ctx.font = font(sizePx);
  const lines = wrapLines(ctx, text, inner);
  return Math.max(1, lines.length) * lineHeight(sizePx) + gapAfter;
}

function measureMedBlock(ctx, med, inner, sizes) {
  const qtyW = med.qtyLine
    ? (() => {
        ctx.font = font(sizes.qty, '500');
        return ctx.measureText(med.qtyLine).width + 10;
      })()
    : 0;
  ctx.font = font(sizes.med, '500');
  const nameLines = wrapLines(ctx, med.indexName, Math.max(40, inner - qtyW));
  let h = nameLines.length * lineHeight(sizes.med) + 4;
  if ((med.detailLines || []).length) {
    (med.detailLines || []).forEach((line) => {
      ctx.font = posologyFont(sizes.posology);
      h += wrapLines(ctx, line, inner - 8).length * lineHeight(sizes.posology) + 1;
    });
  }
  return h + 10;
}

function drawMedItem(ctx, med, y, pad, inner, sizes) {
  let cursor = y;

  ctx.font = font(sizes.qty, '500');
  const qtyW = med.qtyLine ? ctx.measureText(med.qtyLine).width : 0;
  const nameMax = Math.max(40, inner - (qtyW ? qtyW + 12 : 0));

  ctx.font = font(sizes.med, '500');
  const nameLines = wrapLines(ctx, med.indexName, nameMax);
  const nameStart = cursor;
  cursor = drawInkLines(ctx, nameLines, pad, cursor, sizes.med);

  if (med.qtyLine) {
    ctx.font = font(sizes.qty, '500');
    drawInkText(ctx, med.qtyLine, pad + inner - qtyW, nameStart, sizes.qty);
  }

  if ((med.detailLines || []).length) {
    cursor += 3;
    ctx.font = posologyFont(sizes.posology);
    (med.detailLines || []).forEach((line) => {
      const lines = wrapLines(ctx, line, inner - 8);
      cursor = drawInkLines(ctx, lines, pad + 4, cursor, sizes.posology) + 1;
    });
  }

  return cursor + 2;
}

function renderThermalTicketCanvas(data, widthMm, logo = null) {
  const sizes = getSizes();
  const widthPx = Math.round(widthMm * dotsPerMm());
  const pad = 16;
  const inner = widthPx - pad * 2;
  const logoHeight = getLogoHeight();
  const stampHeight = getStampHeight();
  const barcodeCanvas = data.barcodeValue
    ? renderPrescriptionBarcodeCanvas(data.barcodeValue, {
        width: 3,
        height: 80,
        fontSize: 18,
        displayValue: false,
        margin: 8
      })
    : null;
  const patient = resolvePatientFields(data);
  const documentTitle = (data.documentTitle || 'ORDONNANCE').toUpperCase();
  const meds = (data.medications || []).map(resolveMedicationLines);
  const dateValue = data.refParts?.date || '';
  const ordonnanceNumber = data.refParts?.prescriptionNumber || '';
  const referenceText = ordonnanceNumber || (data.previewUnsaved
    ? 'N° attribué après enregistrement'
    : 'N° indisponible');
  const patientRows = getPatientInfoRows(patient, dateValue);
  const sloganLines = splitSloganTwoLines(data.slogan, data.sloganLines);

  const measure = document.createElement('canvas').getContext('2d');
  measure.imageSmoothingEnabled = false;

  const logoColW = Math.floor(inner * 0.36);
  const siteColW = Math.floor(inner * 0.28);
  const centerColW = Math.max(80, inner - logoColW - siteColW - 12);

  measure.font = font(sizes.slogan, '500', true);
  const sloganH = sloganLines.length
    ? sloganLines.reduce((sum, line) => {
        return sum + Math.max(1, wrapLines(measure, line, centerColW).length) * lineHeight(sizes.slogan);
      }, 0) + 4
    : 0;
  measure.font = font(sizes.docTitle, '900');
  const titleH = Math.max(1, wrapLines(measure, documentTitle, centerColW).length) * lineHeight(sizes.docTitle);
  const centerBlockH = sloganH + titleH;

  measure.font = font(sizes.site, '500');
  const siteH = data.siteName
    ? Math.max(1, wrapLines(measure, data.siteName, siteColW).length) * lineHeight(sizes.site)
    : 0;
  const siteBlockH = siteH;
  const logoBlockH = logo ? logoHeight : lineHeight(20);
  const headerRowH = Math.max(logoBlockH, siteBlockH, centerBlockH) + 8;

  let height = pad * 2 + 8;
  height += 14; // top band
  height += headerRowH;
  height += measureBlock(measure, referenceText, inner, sizes.docNumber, 4);
  if (barcodeCanvas) height += barcodeCanvas.height + 8;
  else height += measureBlock(measure, 'Code-barres généré après enregistrement', inner, sizes.legal, 6);
  height += 10;
  height += measurePatientInfoBox(measure, patientRows, inner, sizes);
  height += 8;
  meds.forEach((med, i) => {
    height += measureMedBlock(measure, med, inner, sizes);
    if (i < meds.length - 1) height += 6;
  });
  height += 10;
  if (data.notes) {
    height += lineHeight(sizes.notesLabel) + 4;
    height += measureBlock(measure, data.notes, inner - 8, sizes.notes, 12);
  }
  height += lineHeight(sizes.stampLabel) + 4;
  height += stampHeight + 8;
  if (data.doctorName) height += measureBlock(measure, data.doctorName, Math.floor(inner * 0.82), sizes.stampDoctor, 10);
  height += 14; // bottom band
  (data.footerLines || []).forEach((line) => {
    height += measureBlock(measure, line, inner, sizes.legal, 2);
  });
  height += 8;

  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = Math.ceil(height);
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.textBaseline = 'top';

  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let y = pad;

  const drawCentered = (text, sizePx, gap, italic = false, weight = '600', heavy = false) => {
    ctx.font = font(sizePx, weight, italic);
    const lines = wrapLines(ctx, text, inner);
    y = drawCenteredInkLines(ctx, lines, y, sizePx, pad, inner, { heavy }) + gap;
  };

  // —— Bandeau formulaire ——
  y = drawFormBand(ctx, pad, inner, y, 5);

  // —— Logo | slogan 2 lignes + ORDONNANCE | site ——
  const headerTop = y;
  const logoColWDraw = Math.floor(inner * 0.36);
  const siteColWDraw = Math.floor(inner * 0.28);
  const centerX = pad + logoColWDraw + 6;
  const centerW = Math.max(80, inner - logoColWDraw - siteColWDraw - 12);

  let logoBottom = headerTop;
  if (logo) {
    const drawW = Math.min(logoColWDraw, logo.width * (logoHeight / logo.height));
    const drawH = logo.height * (drawW / logo.width);
    ctx.drawImage(logo, pad, headerTop, drawW, drawH);
    logoBottom = headerTop + drawH;
  } else {
    ctx.font = font(sizes.docTitle, '900');
    drawInkText(ctx, 'FUNTOA', pad, headerTop, sizes.docTitle, { heavy: true });
    logoBottom = headerTop + lineHeight(sizes.docTitle);
  }

  let siteBottom = headerTop;
  if (data.siteName) {
    ctx.font = font(sizes.site, '500');
    const siteLines = wrapLines(ctx, data.siteName, siteColWDraw);
    let siteY = headerTop;
    siteLines.forEach((line) => {
      const w = ctx.measureText(line).width;
      drawInkText(ctx, line, pad + inner - w, siteY, sizes.site);
      siteY += lineHeight(sizes.site);
    });
    siteBottom = siteY;
  }

  let centerY = headerTop;
  if (sloganLines.length) {
    ctx.font = font(sizes.slogan, '500', true);
    sloganLines.forEach((line) => {
      const lines = wrapLines(ctx, line, centerW);
      centerY = drawCenteredInkLines(ctx, lines, centerY, sizes.slogan, centerX, centerW) + 1;
    });
    centerY += 4;
  }

  ctx.font = font(sizes.docTitle, '900');
  const titleLines = wrapLines(ctx, documentTitle, centerW);
  centerY = drawCenteredInkLines(ctx, titleLines, centerY, sizes.docTitle, centerX, centerW, { heavy: true });

  y = Math.max(logoBottom, siteBottom, centerY) + 8;

  drawCentered(referenceText, sizes.docNumber, 4, false, '800', true);
  if (barcodeCanvas) {
    const barcodeX = Math.round((widthPx - barcodeCanvas.width) / 2);
    ctx.drawImage(barcodeCanvas, barcodeX, y);
    y += barcodeCanvas.height + 8;
  } else {
    drawCentered('Code-barres généré après enregistrement', sizes.legal, 6, false, '500');
  }

  y = drawRule(ctx, pad, inner, y, { double: true });

  // —— Informations patient dans un cadre unique ——
  y = drawPatientInfoBox(ctx, patientRows, y, pad, inner, sizes);

  y += 4;
  y = drawRule(ctx, pad, inner, y);

  // —— Médicaments ——
  meds.forEach((med, index) => {
    y = drawMedItem(ctx, med, y, pad, inner, sizes);
    if (index < meds.length - 1) {
      y = drawRule(ctx, pad, inner, y + 2);
    }
  });

  y += 6;
  y = drawRule(ctx, pad, inner, y);

  // —— Notes ——
  if (data.notes) {
    ctx.font = font(sizes.notesLabel, '800');
    drawInkText(ctx, 'NOTES', pad, y, sizes.notesLabel, { heavy: true });
    y += lineHeight(sizes.notesLabel) + 3;
    ctx.font = font(sizes.notes, '600');
    const noteLines = wrapLines(ctx, data.notes, inner);
    y = drawInkLines(ctx, noteLines, pad, y, sizes.notes) + 10;
  }

  // —— Signature (droite, ligne classique) ——
  const stampW = Math.floor(inner * 0.82);
  const stampX = pad + (inner - stampW);
  ctx.font = font(sizes.stampLabel, '700');
  const stampLabel = 'Signature et cachet';
  const labelW = ctx.measureText(stampLabel).width;
  drawInkText(ctx, stampLabel, stampX + Math.max(0, (stampW - labelW) / 2), y, sizes.stampLabel);
  y += lineHeight(sizes.stampLabel) + 4;

  // Zone cachet + ligne de signature
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.25;
  ctx.strokeRect(stampX + 0.5, y + 0.5, stampW - 1, stampHeight - 1);
  const sigLineY = y + stampHeight - 14;
  ctx.fillStyle = INK;
  ctx.fillRect(stampX + 10, sigLineY, stampW - 20, 1.5);
  y += stampHeight + 6;

  if (data.doctorName) {
    ctx.font = font(sizes.stampDoctor, '800');
    const docLines = wrapLines(ctx, data.doctorName, stampW);
    const docStartX = stampX;
    docLines.forEach((line, i) => {
      const w = ctx.measureText(line).width;
      drawInkText(ctx, line, docStartX + Math.max(0, (stampW - w) / 2), y + i * lineHeight(sizes.stampDoctor), sizes.stampDoctor, {
        heavy: true
      });
    });
    y += docLines.length * lineHeight(sizes.stampDoctor) + 8;
  }

  // —— Pied formulaire ——
  y = drawFormBand(ctx, pad, inner, y, 5);

  (data.footerLines || []).forEach((line) => {
    drawCentered(line, sizes.legal, 2, false, '600');
  });

  thresholdCanvas(canvas);
  return canvas.toDataURL('image/png');
}

function getPrintHtml(imageDataUrl, widthMm) {
  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>Ordonnance</title>
  <style>
    @page { size: ${widthMm}mm auto; margin: 0; }
    html, body {
      margin: 0;
      padding: 0;
      width: ${widthMm}mm;
      background: ${PAPER};
    }
    img {
      display: block;
      width: ${widthMm}mm;
      max-width: ${widthMm}mm;
      height: auto;
      margin: 0;
      padding: 0;
      border: 0;
      image-rendering: crisp-edges;
    }
  </style>
</head>
<body>
  <img src="${imageDataUrl}" alt="Ordonnance" />
</body>
</html>`;
}

function getPrintFrame() {
  let frame = document.getElementById(PRINT_FRAME_ID);
  if (frame) return frame;

  frame = document.createElement('iframe');
  frame.id = PRINT_FRAME_ID;
  frame.title = 'Impression ordonnance thermique';
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText =
    'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;left:-9999px;top:0';
  document.body.appendChild(frame);
  return frame;
}

function printViaHiddenFrame(imageDataUrl, widthMm) {
  const frame = getPrintFrame();
  const win = frame.contentWindow;
  if (!win) return false;

  const doc = win.document;
  doc.open();
  doc.write(getPrintHtml(imageDataUrl, widthMm));
  doc.close();

  const triggerPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      window.alert("Impossible de lancer l'impression du ticket thermique.");
    }
  };

  win.addEventListener(
    'afterprint',
    () => {
      doc.open();
      doc.write('');
      doc.close();
    },
    { once: true }
  );

  window.setTimeout(triggerPrint, 200);
  return true;
}

export async function printPrescriptionThermal(ticketData, _format = 'thermal-80') {
  if (!ticketData) return false;

  const widthMm = WIDTH_MM;
  const logoUrl = ticketData.logoUrl || `${process.env.PUBLIC_URL || ''}/logo_vertical_noir.png`;

  let logo = null;
  try {
    logo = await loadLogo(logoUrl);
  } catch {
    logo = null;
  }

  try {
    const imageDataUrl = renderThermalTicketCanvas(ticketData, widthMm, logo);
    return printViaHiddenFrame(imageDataUrl, widthMm);
  } catch {
    return false;
  }
}
